<template>
  <div class="account-settings">
    <v-card :class="`card-container ${darkMode ? 'dark-border' : 'light-border'}`" flat>
      <div>
        <h2>
          {{ $t('general.email') }}
        </h2>

        <v-text-field
          v-if="!me.email"
          v-model="userEmail"
          name="email"
          type="email"
          :placeholder="$t('account.emailAddress')"
          required
        ></v-text-field>
        <v-text-field
          v-else
          v-model="me.email"
          name="email"
          type="email"
          clearable
          disabled
        ></v-text-field>

        <div v-if="!me.email" class="mb-4">
          <v-btn
            class="primary"
            :loading="sendingMsgV3"
            :disabled="!userEmail"
            @click="updateEmail"
          >
            {{ $t('account.updateEmailMsg') }}
          </v-btn>
        </div>
      </div>

      <div>
        <h2>
          {{ $t('general.phone') }}
        </h2>

        <VueTelInput
          ref="phone"
          v-model="formattedPhoneNumber"
          name="phone"
          maxlength="15"
          input-classes="phone-input"
          class="tel-input"
          valid-characters-only
          auto-default-country
          required
          :wrapper-classes="
            !isPhoneValid && me && me.phoneNumber ? 'invalid-phone' : 'phone-input-wrapper'
          "
          :placeholder="$t('phoneLogin.enterPhoneNumber')"
          @validate="isValid"
        ></VueTelInput>
        <span v-if="!isPhoneValid && !me && !me.phoneNumber" class="error--text text-error mb-2">
          {{ $t('phoneLogin.invalid') }}
        </span>
        <transition name="fade" mode="in-out">
          <v-checkbox
            v-if="me && me.phoneNumber"
            v-model="msgConsent"
            color="info"
            class="mt-1"
            hide-details
          >
            <template #label>
              <div class="grey--text text--darken-2">
                {{ $t('general.loginConsentMsg') }}
              </div>
            </template>
          </v-checkbox>
        </transition>
        <div class="mt-2">
          <v-btn
            class="mt-2 primary"
            :loading="sendingMsgV3"
            :disabled="!msgConsent || !isPhoneValid"
            @click="updatePhone"
          >
            {{ $t('account.updatePhoneMsg') }}
          </v-btn>
        </div>

        <div
          ref="turnstile"
          class="mt-4"
          data-theme="light"
          data-appearance="interaction-only"
          action="account-settings"
        ></div>
        <!-- data-size="flexible" -->
      </div>

      <v-divider class="my-8"></v-divider>

      <!-- Update password form -->
      <v-form v-model="valid" class="password-form pt-2" @submit.prevent="changePassword">
        <h2>{{ meV3SetPass ? 'Set Password' : $t('general.changePassword') }}</h2>

        <div v-if="meV3SetPass" class="py-4">
          <v-alert border="left" colored-border type="info" elevation="2">
            {{ $t('general.noPasswordMsg') }}
          </v-alert>
          <v-btn type="submit" class="primary mt-2" :loading="sendingMsgV3">
            {{ $t('general.setPassword') }}
          </v-btn>
          <v-divider v-if="meV3SetPass" class="mt-6"></v-divider>
        </div>

        <div v-else>
          <v-text-field
            v-model="oldPassword"
            name="oldPassword"
            tabindex="1"
            :append-icon="visibleNewPassword ? 'visibility' : 'visibility_off'"
            :type="visibleNewPassword ? 'text' : 'password'"
            :label="$t('general.oldPassword')"
            clearable
            required
            @click:append="() => (visibleNewPassword = !visibleNewPassword)"
          ></v-text-field>
          <v-text-field
            v-model="newPassword"
            name="newPassword"
            tabindex="1"
            :append-icon="visibleNewPassword ? 'visibility' : 'visibility_off'"
            :type="visibleNewPassword ? 'text' : 'password'"
            :label="$t('general.newPassword')"
            :rules="[
              (v) => !!v || 'Password is required',
              (v) => (v && v.length >= 8) || 'Password must be at least 8 characters long',
              (v) => !confirmPassword || v === confirmPassword || 'Passwords must match',
            ]"
            clearable
            required
            @click:append="() => (visibleNewPassword = !visibleNewPassword)"
          ></v-text-field>
          <v-text-field
            v-model="confirmPassword"
            name="confirmPassword"
            tabindex="1"
            :append-icon="visibleNewPassword ? 'visibility' : 'visibility_off'"
            :type="visibleNewPassword ? 'text' : 'password'"
            :label="$t('general.confirmPassword')"
            :rules="[
              (v) => !!v || 'Password is required',
              (v) => !newPassword || v === newPassword || 'Passwords must match',
            ]"
            clearable
            required
            @click:append="() => (visibleNewPassword = !visibleNewPassword)"
          ></v-text-field>
        </div>

        <v-btn v-if="!meV3SetPass" type="submit" class="primary" :disabled="!valid">{{
          $t('general.updatePassword')
        }}</v-btn>
      </v-form>
    </v-card>

    <v-card
      :class="`mt-4 card-container card-container-small ${
        darkMode ? 'dark-border' : 'light-border'
      }`"
      flat
    >
      <v-card-title class="text-subtitle-1 py-1">
        {{ $t('account.logOutAllMsg') }}?
        <v-spacer></v-spacer>
        <v-btn
          small
          text
          :color="`${$vuetify.theme.dark ? '#eee' : 'primary'}`"
          @click="logOutAllStart"
        >
          {{ $t('general.logOutEverywhere') }}
        </v-btn>
      </v-card-title>
    </v-card>

    <v-card
      :class="`mt-4 mb-4 card-container card-container-small ${
        darkMode ? 'dark-border' : 'light-border'
      }`"
      flat
    >
      <v-card-title class="text-subtitle-1 py-1">
        {{ $t('account.deleteTxt') }}
        <v-spacer></v-spacer>
        <v-btn small text color="primary" @click="deleteAccount">
          {{ $t('account.deleteBtnTxt') }}
        </v-btn>
      </v-card-title>
    </v-card>

    <SettingsBottomNav :limited-user="limitedUser" />

    <v-dialog v-model="deleteDialog" transition="dialog-bottom-transition" max-width="550px">
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title class="text-h5 primary white--text delete-title" primary-title>
          {{ $t('general.warning').toUpperCase() }}! {{ $t('account.deleteAccountHeading') }}
        </v-card-title>

        <v-card-text class="text-body-1 pt-6 pb-2 font-weight-bold">
          {{ $t('account.deleteAccountInfo') }}:
          <span
            :class="[
              'info--text text-decoration-underline',
              $vuetify.theme.dark ? 'info--text' : '',
            ]"
          >
            {{ me.email }}
          </span>
        </v-card-text>
        <v-card-text class="text-body-1 pb-6 font-weight-bold">
          {{ $t('account.deleteMsgPt1') }}
        </v-card-text>
        <v-card-text class="text-body-1 pb-6 font-weight-bold">
          {{ $t('account.deleteMsgPt2') }}
        </v-card-text>
        <v-card-text class="text-body-1 pb-6 font-weight-bold">
          {{ $t('account.deleteMsgPt3') }}
        </v-card-text>

        <v-card-text class="pt">
          <v-text-field
            v-model="deleteConfirmPassword"
            data-lpignore="true"
            name="password"
            clearable
            :label="$t('general.confirmPassword')"
            :append-icon="visibleDeleteConfirmNewPassword ? 'visibility' : 'visibility_off'"
            :type="visibleDeleteConfirmNewPassword ? 'text' : 'password'"
            @click:append="
              () => (visibleDeleteConfirmNewPassword = !visibleDeleteConfirmNewPassword)
            "
          ></v-text-field>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :color="$vuetify.theme.dark ? 'white' : 'default'" text @click.stop="cancelDelete">
            {{ $t('general.no') }}
          </v-btn>
          <v-btn
            text
            color="primary"
            :disabled="!deleteConfirmPassword"
            @click.stop="confirmDelete"
          >
            {{ $t('general.yes') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="setPhoneDialog" width="500">
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title
          class="text-h5 primary white--text text-uppercase set-phone-title"
          primary-title
        >
          {{ $t('general.msgSent') }}
        </v-card-title>

        <v-card-text class="text-body-1 pt-2">
          {{ $t('account.smsMsgSent') }}
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="setPhoneDialog = false"> Ok </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="setEmailDialog" width="500">
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title
          class="text-h5 primary white--text text-uppercase set-phone-title"
          primary-title
        >
          {{ $t('general.msgSent') }}
        </v-card-title>

        <v-card-text class="text-body-1 pt-2">
          {{ $t('account.emailMsgSent') }}
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="setEmailDialog = false"> Ok </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="setPassDialog" width="500">
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title
          class="text-h5 primary white--text text-uppercase set-phone-title"
          primary-title
        >
          {{ $t('general.msgSent') }}
        </v-card-title>

        <v-card-text v-if="me && me.email" class="text-body-1 pt-2">
          {{ $t('phoneLogin.emailMsgSent') }}
        </v-card-text>

        <v-card-text v-else class="text-body-1 pt-2">
          {{ $t('account.smsMsgSent') }}
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeSetPassDialog"> Ok </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="logoutDialog" width="500">
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title class="text-h5 primary white--text" style="word-break: break-word">
          {{ $t('info.attention').toUpperCase() }}!
        </v-card-title>

        <v-card-text class="text-subtitle-1 pt-3">
          <h3>{{ $t('account.logOutAllText') }}?</h3>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelLogOut"> {{ $t('general.cancel') }} </v-btn>
          <v-btn color="primary" text @click="resetUserToken"> {{ $t('general.confirm') }} </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- <confirm-password-dialog
      @success="success"
      @failed="failed"
      @cancelled="cancelled"
      :confirm-password-dialog="confirmPasswordDialog"
    ></confirm-password-dialog> -->
  </div>
</template>

<script>
import Router from 'vue-router';
import { VueTelInput } from 'vue-tel-input';
import meMixin from '@/mixins/meMixin';
import utilityMixin from '@/mixins/utilityMixin';
import SettingsBottomNav from '@/components/SettingsBottomNav';
import Configuration from '@/utils/configuration';

const { isNavigationFailure, NavigationFailureType } = Router;
const VUE_APP_TURNSTILE_SITE_KEY = Configuration.value('VUE_APP_TURNSTILE_SITE_KEY');

export default {
  name: 'AccountSettings',

  components: {
    SettingsBottomNav,
    VueTelInput,
  },
  mixins: [meMixin, utilityMixin],

  props: {
    limitedUser: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      valid: true,
      confirmPasswordDialog: false,
      deleteDialog: false,
      setPhoneDialog: false,
      setEmailDialog: false,
      setPassDialog: false,
      logoutDialog: false,

      userEmail: null,

      password: '',
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      deleteConfirmPassword: '',
      visibleNewPassword: false,
      visibleDeleteConfirmNewPassword: false,
      phoneNumberData: null,
      msgConsent: false,
      sendingMsgV3: false,
      turnstileToken: null,
      turnstileWidgetId: null,
      rules: {
        passwordLength: (value) => {
          if (value?.length === 0) {
            return true;
          }
          if (value?.length < 8) {
            return 'Password must be at least 8 characters';
          }
          return true;
        },
      },
    };
  },

  computed: {
    isPhoneValid() {
      return this.phoneNumberData?.valid;
    },

    // formattedPhoneNumber() {
    //   if (this.me?.phoneNumber) {
    //     return this.me?.phoneNumber?.replace('+', '');
    //   }
    // },

    formattedPhoneNumber: {
      get() {
        if (this.me?.phoneNumber) {
          return this.me?.phoneNumber?.replace('+', '');
        }
        return null;
      },
      set(newValue) {
        this.me.phoneNumber = newValue;
        this.msgConsent = true;
        return newValue;
      },
    },
  },

  watch: {
    'me.phoneNumber': function (newValue, oldValue) {
      if (newValue?.length) {
        this.msgConsent = true;
      } else {
        this.msgConsent = false;
      }
    },
  },

  beforeCreate() {
    localStorage.setItem('userUpdated', true);
  },

  mounted() {
    this.renderTurnstile();
  },

  methods: {
    renderTurnstile() {
      this.$nextTick(() => {
        if (window.turnstile && this.$refs.turnstile) {
          this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstile, {
            sitekey: VUE_APP_TURNSTILE_SITE_KEY,
            callback: (token) => {
              this.turnstileToken = token;
            },
            'expired-callback': () => {
              this.turnstileToken = null;
            },
          });
        }
      });
    },

    resetTurnstile() {
      this.turnstileToken = null;
      if (window.turnstile && this.turnstileWidgetId !== null) {
        window.turnstile.reset(this.turnstileWidgetId);
      }
    },

    changePassword() {
      if (this.meV3SetPass) {
        this.requestPasswordReset();
        return;
      }

      const payload = {
        oldPassword: this.oldPassword,
        newPassword: this.newPassword,
      };

      this.$store
        .dispatch('users/changePassword', payload)
        .then((res) => {
          this.oldPassword = '';
          this.newPassword = '';
          this.confirmPassword = '';
          this.$root.vtoast.show({
            text: `Your password has been updated`,
            color: 'success',
          });
        })
        .catch((err) => {
          this.oldPassword = '';
          this.newPassword = '';
          this.confirmPassword = '';
          this.$root.vtoast.show({
            text: err?.data?.detail ?? 'Error updating password',
            color: 'error',
          });
        });
    },

    // Is this used still?
    // success() {
    //   this.confirmPasswordDialog = false;

    //   const payload = {
    //     username: this.me.username,
    //   };

    //   this.$store
    //     .dispatch('users/update', payload)
    //     .then((res) => {
    //       this.$root.vtoast.show({
    //         text: `Your username has been updated`,
    //         color: 'success',
    //       });
    //     })
    //     .catch((err) => {
    //       this.$root.vtoast.show({
    //         text: err?.data?.detail ?? 'Error updating user',
    //         color: 'error',
    //       });
    //     });
    // },

    failed() {
      this.$root.vtoast.show({
        text: 'Password is incorrect',
        color: 'error',
      });
    },

    cancelled() {
      this.confirmPasswordDialog = false;
    },

    // Log out everywhere process ======
    logOutAllStart() {
      this.logoutDialog = true;
    },

    cancelLogOut() {
      this.logoutDialog = false;
    },

    resetUserToken() {
      try {
        this.$store.dispatch('authentication/deleteToken', this.getMe?.id).then(() => {
          this.$store
            .dispatch('authentication/createToken', this.getMe?.id)
            .then(() => {
              this.$root.vtoast.show({
                text: `Sign out successful. Redirecting to login.`,
                color: 'success',
              });
              this.logoutDialog = false;

              setTimeout(() => {
                this.$store.dispatch('authentication/removeAuthentication');
                this.$router.push('/login').catch(() => {});
              }, 2000);
            })
            .catch((err) => {
              console.error('err:', err);
              this.$root.vtoast.show({
                text: `Error signing out`,
                color: 'error',
              });
            });
        });
      } catch (error) {
        console.error('error:', error);
        this.logoutDialog = false;
        this.$root.vtoast.show({
          text: `Error resetting token`,
          color: 'error',
        });
      }
    },

    // Delete account process ===============
    deleteAccount() {
      this.deleteDialog = true;
    },

    cancelDelete() {
      this.deleteDialog = false;
    },

    confirmDelete() {
      if (this.deleteConfirmPassword) {
        this.$store
          .dispatch('users/deleteAccount', { password: this.deleteConfirmPassword })
          .then((res) => {
            this.deleteDialog = false;
            this.$root.vtoast.show({
              text: `User account successfully deleted.`,
              color: 'success',
            });

            setTimeout(() => {
              //* force logout then redirect
              this.$store.dispatch('authentication/logout');
              this.$store.dispatch('users/resetState');
              this.$store.dispatch('licenses/resetLicenses');

              //* remove users preferences from local storage
              localStorage.removeItem('userPreferences');

              this.$router.push('/login').catch((e) => {
                console.error('error:', e);
                if (!isNavigationFailure(e, NavigationFailureType.redirected)) {
                  Promise.reject(e);
                }
              });
            }, 2000);
          })
          .catch((err) => {
            console.error('err:', err);
            this.$root.vtoast.show({
              text: `Error deleting user account`,
              color: 'error',
            });
          });
      }
    },

    isValid(data) {
      if (data) {
        this.phoneNumberData = {
          ...data,
          valid: data?.valid,
          number: data?.number,
          regionCode: data?.regionCode,
        };
        return data?.valid;
      }
    },

    updatePhone() {
      if (!this.turnstileToken) {
        this.$root.vtoast.show({
          text: 'Please complete the security check before continuing.',
          color: 'error',
        });
        return;
      }

      const { number, regionCode } = this.phoneNumberData || {};
      const payload = {
        country_id: regionCode,
        phone: number?.e164,
        param: 'skipCheck',
        ...(this.turnstileToken && { turnstile_token: this.turnstileToken }),
      };

      //* V3 =============================
      this.$store
        .dispatch('authentication/userVerification', payload)
        .then(() => {
          this.sendingMsgV3 = false;
          this.resetTurnstile();
          this.setPhoneDialog = true;
        })
        .catch((err) => {
          console.error('err:', err);
          this.sendingMsgV3 = false;
          this.resetTurnstile();
          this.setPhoneDialog = false;
          const { data } = err;
          const { code, msg } = data?.error || {};

          if (!msg || !code) {
            this.$root.vtoast.show({
              text: `Error adding phone number. Please check the information entered.`,
              color: 'error',
            });
          } else {
            this.$root.vtoast.show({
              text: `Error ${code}: ${msg}`,
              color: 'error',
            });
          }
        });
    },

    updateEmail() {
      if (!this.turnstileToken) {
        this.$root.vtoast.show({
          text: 'Please complete the security check before continuing.',
          color: 'error',
        });
        return;
      }

      const payload = {
        email: this.userEmail,
        ...(this.turnstileToken && { turnstile_token: this.turnstileToken }),
      };

      //* V3 =============================
      this.$store
        .dispatch('authentication/userVerification', payload)
        .then(() => {
          this.sendingMsgV3 = false;
          this.resetTurnstile();
          this.setEmailDialog = true;
        })
        .catch((err) => {
          console.error('err:', err);
          this.sendingMsgV3 = false;
          this.resetTurnstile();
          this.setEmailDialog = false;
          const { data } = err;
          const { code, msg } = data?.error || {};

          if (!msg || !code) {
            this.$root.vtoast.show({
              text: `Error adding email. Please check the information entered.`,
              color: 'error',
            });
          } else {
            this.$root.vtoast.show({
              text: `Error ${code}: ${msg}`,
              color: 'error',
            });
          }
        });
    },

    requestPasswordReset() {
      this.sendingMsgV3 = true;
      const { number, regionCode } = this.phoneNumberData || {};

      if (!this.turnstileToken) {
        this.sendingMsgV3 = false;
        this.$root.vtoast.show({
          text: 'Please complete the security check before continuing.',
          color: 'error',
        });
        return;
      }

      if (!this.me?.email && !number) {
        this.sendingMsgV3 = false;
        alert(
          'No email or phone number set. Please enter an email or phone number to set your password'
        );
        return;
      }

      const payload = {
        reset_password: true,
        ...(this.turnstileToken && { turnstile_token: this.turnstileToken }),
      };

      if (this.me?.email) {
        payload.email = this.me.email;
      } else {
        payload.country_id = regionCode;
        payload.phone = number?.e164;
      }

      //* V3 =============================
      this.$store
        .dispatch('authentication/userVerification', payload)
        .then(() => {
          this.sendingMsgV3 = false;
          this.resetTurnstile();
          this.setPassDialog = true;
        })
        .catch((err) => {
          console.error('err:', err);
          this.sendingMsgV3 = false;
          this.resetTurnstile();
          this.setPassDialog = false;

          if (!err?.data?.error) {
            this.$root.vtoast.show({
              text: `Error setting password. Please check the information entered.`,
              color: 'error',
            });
          } else {
            this.$root.vtoast.show({
              text: `Error: ${err?.data?.error}`,
              color: 'error',
            });
          }
        });
    },

    closeSetPassDialog() {
      this.setPassDialog = false;
      this.sendingMsgV3 = false;
    },
  },
};
</script>

<style scoped>
.account-settings {
  margin-top: -65px;
  min-height: calc(60vh - 65px) !important;
}

.account-settings .card-container {
  padding: 30px !important;
  border-radius: 10px;
  border-radius: var(--border-10);
}
.account-settings .card-container.card-container-small {
  padding: 10px !important;
}

.left-input-space {
  margin-right: 5px;
}

.right-input-space {
  margin-left: 5px;
}

.username-form {
  margin-top: 40px;
}

.password-form {
  margin-top: 32px;
}

.delete-title,
.set-phone-title {
  word-break: normal;
}

.tel-input {
  /* background: white;
  border-radius: 5px;
  border-color: transparent; */
  /* color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  box-shadow: 0px 3px 1px -2px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)),
    0px 2px 2px 0px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)),
    0px 1px 5px 0px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.12)); */
  letter-spacing: 0.009375em;
  min-height: var(--v-input-control-height, 56px);
  border: none;
  border-bottom: 1px solid #222;
  line-height: 20px;
  padding: 8px 0;
  max-width: 100%;
  min-width: 0;
  width: 100%;
  border-color: rgba(0, 0, 0, 0.42);
}

* >>> .theme--dark .tel-input .vti__dropdown-list {
  background-color: var(--grey-darker, #3e3e3e);
}
* >>> .theme--dark .tel-input .vti__dropdown-item.highlighted,
* >>> .theme--dark .tel-input .vti__dropdown:hover {
  background-color: var(--grey-dark, #5e5e5e);
}
* >>> .theme--dark .tel-input .vti__input.phone-input {
  color: #fff;
}
* >>> .theme--dark .tel-input .vti__input.phone-input::placeholder {
  color: var(--grey-lightest, #dddddd);
}

@media (max-width: 600px) {
  .account-settings {
    margin-top: 0px;
  }
}
</style>
